import * as React from 'react';
import { Form, Button, Icon, Select, Col, DatePicker, Row, Checkbox, message } from 'antd';
import { FormComponentProps } from 'antd/lib/form/Form';
import bindAllOfThis from 'src/utils/BindThisHelper'; // 'this' binder
import { withLocalize, LocalizeContextProps, Translate } from 'react-localize-redux';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import MatchmakerApiController from 'src/api/MatchmakerApiController';
import DistrictApiController from 'src/api/DistrictApiController';
import SchoolApiController from 'src/api/SchoolApiController';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import AdminReportRequestDTO from 'src/models/report/AdminReportRequestDTO';
import ArrayUtil from 'src/utils/ArrayUtil';
import { NameOf } from 'src/utils/NameOf';
import { downloadFile } from 'src/utils/DownloadUtil';



interface MatchmakerFormProps extends FormComponentProps, LocalizeContextProps {
}

interface MatchmakerFormState {
    districts: KeyValueDTO<number, string>[];
    schools: KeyValueDTO<number, string>[];

    // Filtering
    filteredDistricts: KeyValueDTO<number, string>[];
    filteredSchools: KeyValueDTO<number, string>[];
    isFilteringDistrict: boolean;
    isFilteringSchool: boolean;

    useSchoolClients: boolean;

    // Loading indicators
    isLoadingDistricts: boolean;
    isLoadingSchools: boolean;
    isSubmitting: boolean;
}

class MatchmakerForm extends React.Component<MatchmakerFormProps, MatchmakerFormState> {
    constructor(props: MatchmakerFormProps) {
        super(props);
        bindAllOfThis(this, MatchmakerForm.prototype);

        this.state = {
            districts: [],
            schools: [],

            filteredDistricts: [],
            filteredSchools: [],
            isFilteringDistrict: false,
            isFilteringSchool: false,

            useSchoolClients: false,
            isLoadingDistricts: true,
            isLoadingSchools: true,
            isSubmitting: false,
        };
    }

    componentDidMount() {
        this.getDistricts();
        this.getSchools();
    }

    private getDistricts() {
        this.setState({ isLoadingDistricts: true });
        return DistrictApiController.getAllDistricts().then((result) => {
            this.setState({
                districts: result.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0),
                isLoadingDistricts: false
            });
        }).catch(e => {
            message.error(<Translate id="admin.addEditDistrict.messageRetrieveError" />);
            this.setState({ isLoadingDistricts: false });
        });
    }

    private getSchools(districtIds?: number[]) {
        let request = districtIds == null || districtIds.length < 1
            ? SchoolApiController.getAllSchoolsForSchoolClientType()
            : SchoolApiController.getSchoolsByDistricts({ listOfInts: districtIds });
        this.setState({ isLoadingSchools: true });
        return request.then((result) => {
            this.setState({
                schools: result.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0),
                isLoadingSchools: false
            });
        }).catch(e => {
            message.error(<Translate id="admin.common.messageSchoolsInDistrictError" />);
            this.setState({ isLoadingSchools: false });
        });
    }

    private onCheckboxChange(value: CheckboxChangeEvent) {
        this.setState({useSchoolClients: !this.state.useSchoolClients});

        // Clear out the districts and schools before continuing
        this.props.form.setFieldsValue({
            districtIds: [],
            schoolIds: []
        } as Partial<AdminReportRequestDTO>);
        this.getSchools().then(() => {
            // Update the school filter list
            this.handleSchoolSearch("");
        });
    }

    private handleDistrictFilter(values: number[]) {
        // Called when an item is selected from the dropdown
        this.getSchools(values).then(() => {
            // Update the school filter list
            this.handleSchoolSearch("");
            this.props.form.setFieldsValue({
                schoolIds: []
            } as Partial<AdminReportRequestDTO>);
        });
    }

    private handleDistrictSearch(value: string) {
        let input = (value || "").toLowerCase();

        // Filter the districts if input is 2+ chars OR if the number of districts is small, like 50 or so
        if (input.length >= 2 || this.state.districts.length < 50) {
            let filteredDistricts = this.state.districts
                .filter(x => x.value.toLowerCase().indexOf(input) >= 0);
            this.setState({
                filteredDistricts: ArrayUtil.Unique(filteredDistricts),
                isFilteringDistrict: true
            });
            return;
        }

        this.setState({
            filteredDistricts: [],
            isFilteringDistrict: false
        });
    }

    private handleDistrictBlur() {
        // Run through the same logic, but make sure we cleared out the text
        this.handleDistrictSearch("");
    }

    private handleSchoolSearch(value: string) {
        let input = (value || "").toLowerCase();

        // Filter the schools if input is 2+ chars OR if the number of schools is small, like 50 or so
        if (input.length >= 2 || this.state.schools.length < 50) {
            let filteredSchools = this.state.schools
                .filter(x => x.value.toLowerCase().indexOf(input) >= 0);
            this.setState({
                filteredSchools: ArrayUtil.Unique(filteredSchools),
                isFilteringSchool: true
            });
            return;
        }

        this.setState({
            filteredSchools: [],
            isFilteringSchool: false
        });
    }

    private handleSchoolBlur() {
        // Run through the same logic, but make sure we cleared out the text
        this.handleSchoolSearch("");
    }

    private submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        // Make sure all fields are valid
        this.props.form.validateFields((errors, values) => {
            // Loading for finish button
            this.setState({
                isSubmitting: !errors
            });

            MatchmakerApiController.getUserDetails(values).then(response => {
                downloadFile(response.data.data, 'Matchmaker.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                this.setState({
                    isSubmitting: false
                });
            });
        });
    }

    render() {
        let localDistrictIds: number[] = this.props.form.getFieldValue(NameOf<AdminReportRequestDTO>("districtIds")) || [];
        let localSchoolIds: number[] = this.props.form.getFieldValue(NameOf<AdminReportRequestDTO>("schoolIds")) || [];
        let schoolsDisabled = !this.state.useSchoolClients && (localDistrictIds.length === 0) || this.state.isLoadingSchools;
        let districtsDisabled = this.state.useSchoolClients || this.state.isLoadingDistricts;

        return <Form layout="vertical" onSubmit={this.submit}>
            <h2>Download Matchmaker File</h2>

            <Row gutter={20}>
                <Col span={6}>
                    <Checkbox onChange={this.onCheckboxChange} checked={this.state.useSchoolClients}>
                        Search by School Only
                    </Checkbox>
                </Col>
            </Row>
            <br />
            <Row gutter={20}>
                <Col span={6}>
                    <Form.Item label={<Translate id="report.admin.districtFilter.label" />}>
                        {this.props.form.getFieldDecorator(NameOf<AdminReportRequestDTO>("districtIds"))
                            (<Select
                                filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                onChange={this.handleDistrictFilter}
                                onSearch={this.handleDistrictSearch}
                                onBlur={this.handleDistrictBlur}
                                placeholder={<Translate id="report.admin.districtFilter.placeholder"/>}
                                allowClear
                                showSearch
                                disabled={districtsDisabled}
                                notFoundContent={this.state.isFilteringDistrict ? "No Results" : "Please type 2 or more characters to search"}
                                mode="multiple">
                                {
                                    this.state.filteredDistricts.map(x => <Select.Option key={x.key} value={x.key} title={x.value}>{x.value}</Select.Option>)
                                }
                            </Select>)}
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label={<Translate id="report.admin.schoolFilter.label" />}>
                        {this.props.form.getFieldDecorator(NameOf<AdminReportRequestDTO>("schoolIds"))
                            (<Select
                                filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                onSearch={this.handleSchoolSearch}
                                onBlur={this.handleSchoolBlur}
                                placeholder={<Translate id="report.admin.schoolFilter.placeholder"/>}
                                allowClear
                                showSearch
                                disabled={schoolsDisabled}
                                notFoundContent={this.state.isFilteringSchool
                                        ? "Not Found"
                                        : this.state.schools.length === 0
                                            ? "No Results from District Filter"
                                            : "Please type 2 or more characters to search" // You have to select ALOT to get this one
                                        }
                                mode="multiple">
                                {
                                    this.state.filteredSchools.map(x => <Select.Option key={x.key} value={x.key} title={x.value}>{x.value}</Select.Option>)
                                }
                            </Select>)}
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="Download Matchmaker File">
                        {
                            <Button
                                type="primary"
                                icon="download"
                                loading={this.state.isSubmitting}
                                disabled={localDistrictIds.length === 0 && localSchoolIds.length === 0}
                                htmlType="submit">
                                Download&nbsp;&nbsp;&nbsp;
                            </Button>
                        }
                    </Form.Item>
                </Col>
            </Row>
        </Form>;
    }
}

export default withLocalize(Form.create()(MatchmakerForm));



// WEBPACK FOOTER //
// ./src/pages/admin/forms/MatchmakerForm.tsx